import type { FilePublicWithPermissions } from '@repo/types/files';

import { useGetFileQuery } from '@repo/api/file/useGetFile';
import { getFileExtension } from '@repo/lib/fs/getFileExtension';
import { AlertTriangleIcon, ArrowLeftIcon, DownloadIcon, PencilIcon, Share2Icon, Trash2Icon } from 'lucide-react';
import { Navigate, useNavigate, useParams } from 'react-router';

import DeleteFSEntry from '@/components/features/files/forms/DeleteFSEntry';
import EditFSEntryName from '@/components/features/files/forms/EditFSEntryName';
import { InfoFsEntryContent } from '@/components/features/files/forms/InfoFSEntry';
import ShareFSEntry from '@/components/features/files/forms/ShareFSEntry';
import { useFileMetadata, ViewerContent } from '@/components/features/files/layouts/files-page/FsMediaViewer';
import { MediaPdfViewerProvider, MediaPDFViewerNavigation, MediaPDFViewerNavigationResize } from '@/components/features/media/MediaPDFViewer';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button, ButtonGroup } from '@/components/ui/button';
import { FileIcon } from '@/components/ui/file-icon';
import { FullscreenRoot, FullscreenTarget } from '@/components/ui/fullscreen';
import { Skeleton } from '@/components/ui/skeleton';
import { useFilesStorage } from '@/store/files-store';

export default function FilePage() {
  const { id } = useParams();

  if (!id) {
    return <Navigate to="/not-found" />;
  }

  return (
    <div className="base-direction-col">
      <FilePageContent id={id} />
    </div>
  );
}

function FilePageContent({ id }: { id: string }) {
  const navigate = useNavigate();
  const { data: file, isPending, isError } = useGetFileQuery(id);
  const { openEditName, setOpenEditName, openShare, setOpenShare, openDelete, setOpenDelete, setSelected } = useFilesStorage();

  const fileAsPublic = file as FilePublicWithPermissions | undefined;
  const { fileUrl, serveType, subtype, isFilesInstance, isPdf } = useFileMetadata(fileAsPublic);

  if (isError) {
    return (
      <div className="viewport-height flex w-full flex-col gap-4 overflow-hidden">
        <header className="border-border-primary bg-background flex w-full shrink-0 items-center gap-2 rounded-2xl border p-4">
          <Button variant="ghost" size="sm" aria-label="Wróć" onClick={() => navigate(-1)}>
            <ArrowLeftIcon />
          </Button>
          <FileIcon size="md" />
        </header>
        <Alert variant="destructive">
          <AlertTriangleIcon />
          <AlertTitle>Błąd</AlertTitle>
          <AlertDescription>Nie udało się załadować pliku.</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <FullscreenRoot>
      <div className="viewport-height flex w-full flex-col gap-4 overflow-hidden">
        <EditFSEntryName open={openEditName} onOpenChange={setOpenEditName} element={file} directoryId={file?.directoryId} />
        <ShareFSEntry open={openShare} onOpenChange={setOpenShare} element={file} directoryId={file?.directoryId ?? ''} />
        <DeleteFSEntry
          elements={{ files: fileAsPublic ? [fileAsPublic] : [], directories: [] }}
          open={openDelete}
          onOpenChange={setOpenDelete}
          directoryId={file?.directoryId}
          callback={() => navigate(-1)}
        />

        <header className="border-border-primary bg-background flex w-full shrink-0 items-center gap-2 rounded-2xl border p-4">
          <Button variant="ghost" size="sm" aria-label="Wróć" onClick={() => navigate(-1)}>
            <ArrowLeftIcon />
          </Button>
          <FileIcon fileExtension={file ? getFileExtension(file.name) : undefined} size="md" />
          {isPending ? <Skeleton className="h-5 w-48 rounded-sm" /> : <h2 className="text-foreground self-center truncate text-xl font-semibold">{file?.name}</h2>}
          <div className="ml-auto flex shrink-0 items-center">
            <ButtonGroup>
              {file && (
                <Button variant="outline" size="sm" asChild>
                  <a href={fileUrl} download aria-label="Pobierz plik">
                    <DownloadIcon />
                  </a>
                </Button>
              )}
              {!!file?.permissions?.edit && (
                <Button variant="outline" size="sm" aria-label="Udostępnij" onClick={() => setOpenShare(true)}>
                  <Share2Icon />
                </Button>
              )}
              {!!file?.permissions?.edit && (
                <Button variant="outline" size="sm" aria-label="Edytuj nazwę" onClick={() => setOpenEditName(true)}>
                  <PencilIcon />
                </Button>
              )}
              {!!file?.permissions?.write && (
                <Button
                  variant="outline"
                  size="sm"
                  aria-label="Usuń"
                  variantColor={'destructive'}
                  onClick={() => {
                    if (fileAsPublic) {
                      setSelected({ files: [fileAsPublic], directories: [] });
                    }
                    setOpenDelete(true);
                  }}
                >
                  <Trash2Icon />
                </Button>
              )}
            </ButtonGroup>
          </div>
        </header>

        <div className="flex min-h-0 flex-1 flex-col gap-4 lg:flex-row">
          <div className="border-border-primary flex flex-1 flex-col overflow-hidden rounded-lg border">
            <FullscreenTarget className="flex min-h-0 flex-1 flex-col" fullscreenClassName="fixed inset-0 z-50 h-screen w-screen rounded-none bg-background">
              {fileAsPublic ? (
                <MediaPdfViewerProvider file={fileAsPublic}>
                  {isPdf && (
                    <div className="bg-background flex shrink-0 items-center justify-between border-b px-3 py-2">
                      <MediaPDFViewerNavigation />
                      <MediaPDFViewerNavigationResize />
                    </div>
                  )}
                  <div className="bg-background flex min-h-0 flex-1 flex-col overflow-hidden">
                    <ViewerContent file={fileAsPublic} fileUrl={fileUrl} serveType={serveType} subtype={subtype} isFilesInstance={isFilesInstance} />
                  </div>
                </MediaPdfViewerProvider>
              ) : (
                <div className="bg-card flex min-h-0 flex-1 flex-col overflow-hidden">{isPending && <Skeleton className="h-full w-full" />}</div>
              )}
            </FullscreenTarget>
          </div>

          <div className="bg-background border-border-primary w-full overflow-y-auto rounded-lg border lg:w-80 lg:shrink-0">
            {isPending ? <SidebarSkeleton /> : <InfoFsEntryContent element={fileAsPublic} />}
          </div>
        </div>
      </div>
    </FullscreenRoot>
  );
}

function SidebarSkeleton() {
  return (
    <div className="space-y-8 p-3">
      <div className="space-y-2">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-3/4" />
        <Skeleton className="h-3 w-1/2" />
      </div>
      <div className="space-y-2">
        <Skeleton className="h-4 w-20" />
        <div className="flex items-center gap-2 py-2">
          <Skeleton className="h-10 w-10 rounded-sm" />
          <Skeleton className="h-3 w-14" />
          <Skeleton className="ml-auto h-3 w-11" />
        </div>
      </div>
    </div>
  );
}
